import React, { useEffect } from "react";
import { CheckboxCopy, Form, NameContainer, StyledField, SubmitButton } from "../../../styles/styled";
import { Checkbox, Dropdown, FilteredDropdown, TextInput } from "shared-react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { BusinessDetailsFormData, DropdownItem } from "../../../util/interfaces";
import {
  employeeCounts,
  usStates,
  yearlyRevenueOptions,
  orgTypes
} from "../../../util/constants";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { yupResolver } from "@hookform/resolvers/yup";
import { AppEffects } from "../../../store/app";
import { CustomerOption, FormCardH2, Spacer } from "../index";

interface BusinessDetailsFormProps {
  validateAndContinue: SubmitHandler<BusinessDetailsFormData>;
}

const stateOptions: DropdownItem[] = [{value: '', label: 'Select State', hidden: true}];
for (let state of usStates) {
  stateOptions.push({value: state.name, label: state.name});
}
const employeeCountOptions: DropdownItem[] = [{value: '', label: 'Select Count', hidden: true}];
for (let count of employeeCounts) {
  employeeCountOptions.push({value: count.value, label: count.label});
}
const annualRevenueOptions: DropdownItem[] = [{value: '', label: 'Select Revenue', hidden: true}];
for (let option of yearlyRevenueOptions) {
  annualRevenueOptions.push(option);
}
const orgTypeOptions: DropdownItem[] = [{value: '', label: 'Select Organization Type', hidden: true}];
for (let option of orgTypes) {
  orgTypeOptions.push(option);
}

const schema = yup.object().shape({
  organizationName: yup.string().required('Please enter the name of your organization.'),
  country: yup.string().required('Please enter your country of residence.'),
  state: yup.string().when('country', {
    is: 'United States',
    then: yup.string().required('Please enter your state of residence.')
  }),
  industry: yup.string().required('Please select an industry.'),
  orgType: yup.string().required('Please select an organization type.'),
  website: yup.string().matches(
    /((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
    'Please enter a valid website.'
  ).required('Website is required.'),
  B2B: yup.boolean(),
  B2C: yup.boolean(),
  customerType: yup.string().test(
    'customerType',
    'Customer Type is required.',
    function() {
      const { B2B, B2C } = this.parent;
      return B2B || B2C;
    }
  ),
  yearlyRevenue: yup.string().not(['Default Value']).required('Annual revenue is required.'),
  employeeCount: yup.string().not(['Default Value']).required('Employee count is required.'),
});

const BusinessDetailsForm: React.FC<BusinessDetailsFormProps> = ({ validateAndContinue }) => {
  const dispatch = useDispatch();
  const industries = useSelector((state: RootState) => state.app.industries);
  const countries = useSelector((state: RootState) => state.app.countries);
  const queryParams = useSelector((state: RootState) => state.app.queryParams);
  const { register, handleSubmit, watch, control, setValue, getValues, formState: { errors }, trigger } =useForm(
    {
      resolver: yupResolver(schema)
    });

  const industryOptions: DropdownItem[] = [{value: '', label: 'Select Industry', hidden: true}];
  for (let industry of industries) {
    industryOptions.push({value: industry.name, label: industry.name, alts: industry.alts});
  }
  const countryOptions: DropdownItem[] = [{value: '', label: 'Select Country', hidden: true}];
  for (let country of countries) {
    countryOptions.push({value: country.name, label: country.name, alts: country.alts});
  }

  const isValidFactory = (optionsArray: DropdownItem[]) => {
    return (target: string) => {
      return optionsArray.some((option) => option.value.toLowerCase() === target.toLowerCase());
    }
  };

  // These are being used to ensure country/state are only set if they are valid options
  const isValidCountry = isValidFactory(countryOptions);
  const isValidState = isValidFactory(stateOptions);

  // this ensures that customerType removes its error after submit is clicked and a selection is made
  const B2B = watch("B2B");
  const B2C = watch("B2C");

  useEffect(() => {
    if (B2B || B2C) {
      trigger("customerType");
    }
  }, [B2B, B2C, trigger]);

  useEffect(() => {
    dispatch(AppEffects.getIndustries());
    dispatch(AppEffects.getCountries());
    // the query params are not always parsed at render time, so this logic must be added to useEffect
    const {organizationName, country, state, customerType} = queryParams;
    // forced to do it this way because poynt passes givenName and familyName back as firstName and Last Name
    organizationName && setValue('organizationName', decodeURIComponent(organizationName));
    country && isValidCountry(decodeURIComponent(country)) && setValue('country', decodeURIComponent(country));
    state && isValidState(decodeURIComponent(state)) && setValue('state', decodeURIComponent(state));
    if(queryParams.customerType) {
      customerType.includes('b2b') && setValue('B2B', true);
      customerType.includes('b2c') && setValue('B2C', true);
    }
  }, [queryParams, isValidCountry, isValidState, setValue, dispatch]);

  return (
    <Form onSubmit={handleSubmit(validateAndContinue)}>
      <>
        <FormCardH2>Business Details</FormCardH2>
        <StyledField errorMessage={errors.organizationName?.message} error={errors.organizationName} label="Legal Business Name">
          <TextInput aria-label="Company" autoComplete="disable" {...register('organizationName')}/>
        </StyledField>
        <StyledField errorMessage={errors.country?.message} error={errors.country} label="Select a Country">
          <Controller name="country" control={control}
                      render={({ field: { onChange, ref } }) => {
                        return <FilteredDropdown ref={ref} value={getValues().country} onChange={onChange} aria-label="Select a Country" autoComplete="disable" defaultValue={countryOptions[0].value} items={countryOptions}/>
                      }
                      } />
        </StyledField>
        {watch('country') === 'United States' &&
        <StyledField errorMessage={errors.state?.message} error={errors.state} label="State of Residence">
          <Controller name="state" control={control}
                      render={({ field: { onChange, ref } }) => {
                        return <FilteredDropdown ref={ref} value={getValues().state} onChange={onChange} autoComplete="disable" aria-label="State of Residence" defaultValue={stateOptions[0].value} items={stateOptions}/>
                      }
                      } />
        </StyledField>}
        <StyledField errorMessage={errors.industry?.message} error={errors.industry} label="Industry">
          <Controller name="industry" control={control}
                      render={({ field: { onChange, ref } }) => {
                        return <FilteredDropdown ref={ref} value={getValues().industry} onChange={onChange} aria-label="Select an Industry" autoComplete="disable" defaultValue={industryOptions[0].value} items={industryOptions}/>
                      }
                      } />
        </StyledField>
        <StyledField errorMessage={errors.orgType?.message} error={errors.orgType} label="Organization Type">
          <Controller name="orgType" control={control}
                      render={({ field: { onChange, ref } }) => {
                        return <FilteredDropdown ref={ref} value={getValues().orgType} onChange={onChange} aria-label="Select an Organization Type" autoComplete="disable" defaultValue={orgTypeOptions[0].value} items={orgTypeOptions}/>
                      }
                      } />
        </StyledField>
        <NameContainer>
          <StyledField errorMessage={errors.employeeCount?.message} error={errors.employeeCount} label="Employee Count">
            <Controller name="employeeCount" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <Dropdown ref={ref} onChange={onChange} aria-label="Employee Count" defaultValue={employeeCountOptions[0].value} items={employeeCountOptions}/>
                        }
                        } />
          </StyledField>
          <Spacer/>
          <StyledField errorMessage={errors.yearlyRevenue?.message} error={errors.yearlyRevenue} label="Annual Revenue">
            <Controller name="yearlyRevenue" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <Dropdown ref={ref} onChange={onChange} aria-label="Annual Revenue" defaultValue={annualRevenueOptions[0].value} items={annualRevenueOptions}/>
                        }
                        } />
          </StyledField>
        </NameContainer>
        <StyledField errorMessage={errors.customerType?.message} error={errors.customerType} label="Who are your customers">
          <CustomerOption>
            <Controller name="B2C" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return (
                            <Checkbox checked={getValues().B2C} aria-label="Consumer Checkbox" onChange={onChange} ref={ref}/>

                          )
                        }} />
            <CheckboxCopy>Consumers</CheckboxCopy>
          </CustomerOption>
          <CustomerOption>
            <Controller name="B2B" control={control}
                        render={({ field: { onChange, ref } }) => {
                          return <Checkbox checked={getValues().B2B} aria-label="Business Checkbox" onChange={onChange} ref={ref}/>
                        }} />
            <CheckboxCopy>Businesses</CheckboxCopy>
          </CustomerOption>
        </StyledField>
        <StyledField errorMessage={errors.website?.message} error={errors.website} label="Website">
          <TextInput aria-label="Website" type="website" readOnly={!!queryParams.website} autoComplete="disable" {...register('website')}/>
        </StyledField>
        <SubmitButton type="submit" role="button" aria-label="continue">Continue</SubmitButton>
      </>
    </Form>
  );
};

export default BusinessDetailsForm;
